import { FieldInstruction } from 'containers/FormBuilder'
import {
    FIELD_GREATER_THAN_ZERO,
    FIELD_REQUIRED,
    FIELD_TOO_LONG,
    FIELD_MAX_NUMBER_OF_ENGINES,
    FIELD_MIN_NUMBER_OF_ENGINES,
    FIELD_UNSUPPORTED_COLLATERAL_ON_REPOWER,
    FIELD_INVALID_CHARACTERS,
} from 'form/validation/validationMessages'
import AssetType from 'state/enums/AssetType'
import * as Yup from 'yup'

import MarineWriteInBaseField from '../fieldComponents/MarineWriteInBaseField'

export type MarineCollateralFormValues = {
    condition: string
    year: string
    make: string
    model: string
    boatType?: string
}

export const DEFAULT_MARINE_COLLATERAL_VALUES: MarineCollateralFormValues = {
    condition: 'N',
    year: '',
    make: '',
    model: '',
}

// Common schema for condition, year, make, model fields
export const baseCollateralSchema = Yup.object().shape({
    condition: Yup.string()
        .required(FIELD_REQUIRED)
        .oneOf(['N', 'U'], FIELD_REQUIRED),
    year: Yup.number().required(FIELD_REQUIRED).moreThan(0, FIELD_REQUIRED),
    make: Yup.string().required(FIELD_REQUIRED),
    model: Yup.string()
        .required(FIELD_REQUIRED)
        .max(200, FIELD_TOO_LONG(200))
        .matches(/^[A-Za-z0-9 \-.()/&,'"*+!:#]*$/, FIELD_INVALID_CHARACTERS),
})

// Hull schema (extends base with boatType)
export const hullSchema = baseCollateralSchema.shape({
    boatType: Yup.string().required(FIELD_REQUIRED),
})

const isNullCollateralValidator = (collateral: string) =>
    Yup.object()
        .nullable()
        .test(
            'is-null',
            FIELD_UNSUPPORTED_COLLATERAL_ON_REPOWER(collateral),
            (value) => value === null,
        )

// Engine schema (same as base)
const engineSchema = baseCollateralSchema
// Trailer schema (same as base, but nullable)
const trailerSchema = baseCollateralSchema.nullable()

export const marineWriteInSchema = Yup.object().shape({
    hull: Yup.object().when('repower', {
        is: false,
        then: hullSchema.nullable().required(FIELD_REQUIRED),
        otherwise: isNullCollateralValidator('Hull'),
    }),
    engines: Yup.array().when('repower', {
        is: false,
        then: Yup.array()
            .of(engineSchema)
            .max(2, FIELD_MAX_NUMBER_OF_ENGINES(2)),
        otherwise: Yup.array()
            .of(engineSchema)
            .required(FIELD_REQUIRED)
            .min(1, FIELD_MIN_NUMBER_OF_ENGINES(1))
            .max(2, FIELD_MAX_NUMBER_OF_ENGINES(2)),
    }),
    trailer: Yup.object().when('repower', {
        is: false,
        then: trailerSchema,
        otherwise: isNullCollateralValidator('Trailer'),
    }),
    totalUnitValue: Yup.number()
        .required(FIELD_REQUIRED)
        .moreThan(0, FIELD_GREATER_THAN_ZERO),
    primaryResidence: Yup.bool().when('repower', {
        is: false,
        then: Yup.bool().required(FIELD_REQUIRED).oneOf([true], FIELD_REQUIRED),
        // Not required when repower is true
    }),
    disclosureMessage: Yup.bool()
        .required(FIELD_REQUIRED)
        .oneOf([true], FIELD_REQUIRED),
    repower: Yup.bool().required(FIELD_REQUIRED),
})

export const marineWriteIn: FieldInstruction<typeof MarineWriteInBaseField> = {
    Component: MarineWriteInBaseField,
    config: {
        initialValue: {
            hull: {
                ...DEFAULT_MARINE_COLLATERAL_VALUES,
                boatType: '',
            },
            engines: [],
            trailer: null,
            totalUnitValue: '',
            primaryResidence: false,
            disclosureMessage: false,
            repower: false,
        },
        assetType: AssetType.MARINE,
        validator: marineWriteInSchema,
    },
}
